import fetchData from "@/utils/fetchData";
import uploadDocument, {
  MAX_UPLOAD_BYTES,
  ACCEPTED_DOCUMENTS,
  ACCEPTED_IMAGES,
} from "@/utils/uploadDocument";
import { resizeImage } from "@/utils/resizeImage";
import { isShortPaste, pastedTextFile } from "@/utils/pasteContext";

export const MAX_DOCUMENTS = 5;
export const MAX_IMAGES = 4;
// Context questions refresh on their own once the user pauses
const CONTEXT_QUESTION_DELAY = 1500;
let contextQuestionTimer = null;

function hasContext(state) {
  return (
    state.context.trim().length > 5 ||
    state.documents.length > 0 ||
    state.images.length > 0
  );
}

function extensionOf(file) {
  return (/\.[^.]+$/.exec(file.name) || [""])[0].toLowerCase();
}

function isAccepted(file) {
  return ACCEPTED_DOCUMENTS.split(",").includes(extensionOf(file));
}

function isImage(file) {
  return ACCEPTED_IMAGES.split(",").includes(extensionOf(file));
}

export default {
  name: "context",
  namespaced: true,
  state: {
    context: "",
    changedAfterQuestionsGenerated: false,
    documents: [],
    // Photos and screenshots, resized: [{ name, dataUrl }]
    images: [],
    // Search the web for facts before writing the make
    research: false,
    // Add the user's profile details (name, company...) to this make
    useProfile: true,
    uploading: 0,
    uploadError: "",
    // The user's library of kept documents: [{ id, name, characters, truncated }]
    savedDocuments: [],
  },
  mutations: {
    SET_CONTEXT(state, payload) {
      state.context = payload;
    },
    SET_CHANGED_AFTER_QUESTIONS_GENERATED(state, payload) {
      state.changedAfterQuestionsGenerated = payload;
    },
    SET_DOCUMENTS(state, payload) {
      state.documents = payload;
    },
    ADD_DOCUMENT(state, payload) {
      state.documents.push(payload);
    },
    REMOVE_DOCUMENT(state, index) {
      state.documents.splice(index, 1);
    },
    SET_IMAGES(state, payload) {
      state.images = payload;
    },
    SET_RESEARCH(state, payload) {
      state.research = payload;
    },
    SET_USE_PROFILE(state, payload) {
      state.useProfile = payload;
    },
    ADD_IMAGE(state, payload) {
      state.images.push(payload);
    },
    REMOVE_IMAGE(state, index) {
      state.images.splice(index, 1);
    },
    CHANGE_UPLOADING(state, change) {
      state.uploading = Math.max(0, state.uploading + change);
    },
    SET_UPLOAD_ERROR(state, payload) {
      state.uploadError = payload;
    },
    SET_SAVED_DOCUMENTS(state, payload) {
      state.savedDocuments = payload;
    },
    MARK_DOCUMENT_SAVED(state, { index, id }) {
      if (state.documents[index]) {
        state.documents[index] = { ...state.documents[index], savedId: id };
      }
    },
  },
  actions: {
    reset({ commit }) {
      clearTimeout(contextQuestionTimer);
      commit("SET_CONTEXT", "");
      commit("SET_CHANGED_AFTER_QUESTIONS_GENERATED", false);
      commit("SET_DOCUMENTS", []);
      commit("SET_IMAGES", []);
      commit("SET_RESEARCH", false);
      commit("SET_USE_PROFILE", true);
      commit("SET_UPLOAD_ERROR", "");
    },
    addContext({ commit, dispatch, state }, context) {
      // Syncing the input from a restored page isn't a change
      if (context === state.context) return;
      commit("SET_CHANGED_AFTER_QUESTIONS_GENERATED", true);
      commit("SET_CONTEXT", context);
      dispatch("scheduleContextQuestions");
    },
    // Replace unanswered context questions with ones about the new context
    scheduleContextQuestions({ dispatch, commit, state }) {
      clearTimeout(contextQuestionTimer);
      if (!hasContext(state)) {
        dispatch("questions/clearUnansweredContextQuestions", null, {
          root: true,
        });
        return;
      }
      commit("questions/SET_CONTEXT_QUESTION_LOADING", true, { root: true });
      contextQuestionTimer = setTimeout(() => {
        contextQuestionTimer = null;
        dispatch(
          "questions/addContextQuestions",
          { replaceQuestions: true },
          { root: true }
        );
      }, CONTEXT_QUESTION_DELAY);
    },
    resetChangedAfterQuestionsGenerated({ commit }) {
      commit("SET_CHANGED_AFTER_QUESTIONS_GENERATED", false);
    },
    // Pasted pictures and long text are attached to read; a short snippet joins the notes
    async addPasted({ dispatch, state }, { files = [], text = "" }) {
      const pasted = text.trim();
      if (files.length) {
        await dispatch("uploadDocuments", files);
      } else if (pasted && !isShortPaste(pasted)) {
        await dispatch("uploadDocuments", [pastedTextFile(pasted)]);
      } else if (pasted) {
        const notes = state.context.replace(/\s+$/, "");
        await dispatch("addContext", notes ? `${notes}\n${pasted}` : pasted);
      }
    },
    async uploadDocuments({ commit, dispatch, state }, files) {
      commit("SET_UPLOAD_ERROR", "");
      const uploads = [];
      for (const file of files) {
        if (isImage(file)) {
          uploads.push(dispatch("addImage", file));
          continue;
        }
        if (state.documents.length + state.uploading >= MAX_DOCUMENTS) {
          commit(
            "SET_UPLOAD_ERROR",
            `You can attach up to ${MAX_DOCUMENTS} documents`
          );
          break;
        }
        if (!isAccepted(file)) {
          commit(
            "SET_UPLOAD_ERROR",
            `${file.name} isn't supported, try a PDF, Word document or text file`
          );
          continue;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
          commit("SET_UPLOAD_ERROR", `${file.name} is larger than 10MB`);
          continue;
        }
        commit("CHANGE_UPLOADING", 1);
        uploads.push(
          uploadDocument(file).then(({ status, document, message }) => {
            commit("CHANGE_UPLOADING", -1);
            if (status === 201 && document) {
              commit("ADD_DOCUMENT", document);
              commit("SET_CHANGED_AFTER_QUESTIONS_GENERATED", true);
              dispatch("scheduleContextQuestions");
            } else if (status === 429) {
              commit(
                "SET_UPLOAD_ERROR",
                "Too many uploads, please wait a moment"
              );
            } else {
              commit(
                "SET_UPLOAD_ERROR",
                message || `We couldn't read ${file.name}, please try again`
              );
            }
          })
        );
      }
      await Promise.all(uploads);
    },
    // Reads a web page and attaches its text like a document
    async addLink({ commit, dispatch, state }, url) {
      commit("SET_UPLOAD_ERROR", "");
      if (!url.trim()) return false;
      if (state.documents.length + state.uploading >= MAX_DOCUMENTS) {
        commit(
          "SET_UPLOAD_ERROR",
          `You can attach up to ${MAX_DOCUMENTS} documents`
        );
        return false;
      }
      commit("CHANGE_UPLOADING", 1);
      const { response } = await fetchData(
        "upload/link",
        { url: url.trim() },
        false
      );
      commit("CHANGE_UPLOADING", -1);
      const document = response.result?.document;
      if (response.status === 201 && document) {
        commit("ADD_DOCUMENT", document);
        commit("SET_CHANGED_AFTER_QUESTIONS_GENERATED", true);
        dispatch("scheduleContextQuestions");
        return true;
      }
      commit(
        "SET_UPLOAD_ERROR",
        response.status === 429
          ? "Too many uploads, please wait a moment"
          : response.result?.message || "We couldn't read that page"
      );
      return false;
    },
    async loadSavedDocuments({ commit, rootState }) {
      if (!rootState.auth.loggedIn) return;
      const { response } = await fetchData(
        "user/documents/list",
        {},
        false,
        false,
        false
      );
      if (
        response.status === 201 &&
        Array.isArray(response.result?.documents)
      ) {
        commit("SET_SAVED_DOCUMENTS", response.result.documents);
      }
    },
    async saveDocument({ commit, state }, index) {
      const document = state.documents[index];
      if (!document || document.savedId) return;
      commit("SET_UPLOAD_ERROR", "");
      const { response } = await fetchData(
        "user/documents/save",
        { name: document.name, text: document.text },
        false,
        false,
        false
      );
      const saved = response.result?.document;
      if (response.status === 201 && saved) {
        commit("MARK_DOCUMENT_SAVED", { index, id: saved.id });
        commit("SET_SAVED_DOCUMENTS", [saved, ...state.savedDocuments]);
      } else {
        commit(
          "SET_UPLOAD_ERROR",
          response.result?.message || `We couldn't save ${document.name}`
        );
      }
    },
    async attachSavedDocument({ commit, dispatch, state }, id) {
      if (state.documents.some((document) => document.savedId === id)) return;
      if (state.documents.length + state.uploading >= MAX_DOCUMENTS) {
        commit(
          "SET_UPLOAD_ERROR",
          `You can attach up to ${MAX_DOCUMENTS} documents`
        );
        return;
      }
      commit("SET_UPLOAD_ERROR", "");
      commit("CHANGE_UPLOADING", 1);
      const { response } = await fetchData(
        "user/documents/get",
        { id },
        false,
        false,
        false
      );
      commit("CHANGE_UPLOADING", -1);
      const document = response.result?.document;
      if (response.status === 201 && document) {
        commit("ADD_DOCUMENT", {
          name: document.name,
          text: document.text,
          characters: document.characters,
          truncated: document.truncated,
          savedId: document.id,
        });
        commit("SET_CHANGED_AFTER_QUESTIONS_GENERATED", true);
        dispatch("scheduleContextQuestions");
      } else {
        commit("SET_UPLOAD_ERROR", "We couldn't open that document");
      }
    },
    async deleteSavedDocument({ commit, state }, id) {
      await fetchData("user/documents/delete", { id }, false, false, false);
      commit(
        "SET_SAVED_DOCUMENTS",
        state.savedDocuments.filter((document) => document.id !== id)
      );
      state.documents.forEach((document, index) => {
        if (document.savedId === id) {
          commit("MARK_DOCUMENT_SAVED", { index, id: undefined });
        }
      });
    },
    async addImage({ commit, dispatch, state }, file) {
      if (state.images.length >= MAX_IMAGES) {
        commit("SET_UPLOAD_ERROR", `You can attach up to ${MAX_IMAGES} photos`);
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        commit("SET_UPLOAD_ERROR", `${file.name} is larger than 10MB`);
        return;
      }
      commit("CHANGE_UPLOADING", 1);
      try {
        const dataUrl = await resizeImage(file);
        if (state.images.length < MAX_IMAGES) {
          commit("ADD_IMAGE", { name: file.name, dataUrl });
          commit("SET_CHANGED_AFTER_QUESTIONS_GENERATED", true);
          dispatch("scheduleContextQuestions");
        }
      } catch {
        commit("SET_UPLOAD_ERROR", `We couldn't open ${file.name}`);
      }
      commit("CHANGE_UPLOADING", -1);
    },
    removeImage({ commit, dispatch }, index) {
      commit("REMOVE_IMAGE", index);
      commit("SET_CHANGED_AFTER_QUESTIONS_GENERATED", true);
      dispatch("scheduleContextQuestions");
    },
    removeDocument({ commit, dispatch }, index) {
      commit("REMOVE_DOCUMENT", index);
      commit("SET_CHANGED_AFTER_QUESTIONS_GENERATED", true);
      dispatch("scheduleContextQuestions");
    },
  },
  modules: {},
  getters: {
    // What gets sent with each request
    documentsForRequest(state) {
      return state.documents.map(({ name, text }) => ({ name, text }));
    },
    imagesForRequest(state) {
      return state.images.map(({ name, dataUrl }) => ({ name, dataUrl }));
    },
  },
};
